import React, { useState } from "react";
import "./PGAModelBuilderPage.css";
import Tabs from "../../../../Tabs";
import PGAModelBuilderSection from "./PGAModelBuilderSection";
import FlexBreak from "../../../../FlexBreak";
import Select from "react-select";
import SelectField from "../../../../SelectField";
import PGAModelBuilderButton from "./PGAModelBuilderButton";
import PGAModelBuilderInput from "./PGAModelBuilderInput";
import PGAModelBuilderInputConfig from "./PGAModelBuilderInputConfig";
import PGAModelBuilderSlateSelect from "./PGAModelBuilderSlateSelect";
import PGAModelBuilderField from "./PGAModelBuilderField";
import LoadingOverlay from "react-loading-overlay";

import { graphql, commitMutation } from "react-relay";
import relayEnvironment from "../../../../../RelayEnvironment";
import PGAModelBuilderCalcTabs from "./PGAModelBuilderCalcTabs";
import PGAModelBuilderOutput from "./PGAModelBuilderOutput";
import ShowHide from "../../../../ShowHide";
import WarningBanner from "../../../../WarningBanner";
import { gql, useQuery } from "@apollo/client";
import { GET_TOOL_URL } from "../../__rtools/queries";
import { Redirect } from "react-router-dom";
import { isToolSub } from "../../../../../AuthUtils";
import DraftkingsInfoOverlay from "../../../../DraftKingsInfoOverlay";
import PGAModelBuilderNewCalcSelect from "./PGAModelBuilderNewCalcSelect";
import Overlay from "../../../../Overlay";

import { ReactComponent as XMark } from "../../../../../assets/icons/x-mark.svg";
import PGAModelBuilderLoadFilters from "./PGAModelBuilderLoadFilters";

const GET_OR_CREATE_MODEL = graphql`
  mutation PGAModelBuilderPageGetOrCreateModelMutation(
    $input: PGATourGetOrCreateModelForSlateInput!
  ) {
    pgaTourGetOrCreateModelForSlate(input: $input) {
      model {
        id

        fairwayGrassTypes {
          edges {
            node {
              id
              categoryName
            }
          }
        }

        roughGrassTypes {
          edges {
            node {
              id
              categoryName
            }
          }
        }

        greenGrassTypes {
          edges {
            node {
              id
              categoryName
            }
          }
        }

        numRounds
        minRounds
        fromDate
        toDate
        minYards
        maxYards
        courses(first: 150) {
          edges {
            node {
              id
              name
            }
          }
        }
        coursePars
      }
    }
  }
`;

const ADD_CALC = graphql`
  mutation PGAModelBuilderPageAddCalcMutation(
    $input: PGATourModelGetOrCreateCalcFeatureInput!
  ) {
    pgaTourModelCreateCalcFeature(input: $input) {
      success
    }
  }
`;

const UPDATE_INPUTS = graphql`
  mutation PGAModelBuilderPageUpdateInputsMutation(
    $input: PGATourModelUpdateInputsInput!
  ) {
    pgaTourModelUpdateInputs(input: $input) {
      success
    }
  }
`;

const LOAD_INPUT = graphql`
  mutation PGAModelBuilderPageLoadInputMutation(
    $input: PGATourLoadSavedModelFilterInput!
  ) {
    pgaTourLoadSavedModelFilter(input: $input) {
      success
    }
  }
`;

const GET_WHO_AM_I = gql`
  {
    whoAmI {
      firstName
      lastName
      fullName
      username
      groups {
        name
      }
      displayName
      slackEmail
    }
  }
`;

const RESET_MODEL = graphql`
  mutation PGAModelBuilderPageResetModelMutation(
    $input: PGATourResetModelInput!
  ) {
    pgaTourResetModel(input: $input) {
      success
    }
  }
`;

const SAVE_FILTERS = graphql`
  mutation PGAModelBuilderPageSaveFiltersMutation(
    $input: PGATourSaveModelFiltersInput!
  ) {
    pgaTourSaveModelFilters(input: $input) {
      success
    }
  }
`;

export default function PGAModelBuilderPage() {
  const [selectedFieldOption, setSelectedFieldOption] = useState("Field Only");
  const [selectedSlateId, setSelectedSlateId] = useState(undefined);
  const [pgaTourModelId, setPGATourModelId] = useState(undefined);
  const [inputReloads, setInputReloads] = useState(0);
  const [modelResetting, setModelResetting] = useState(false);

  const [selectedFairwayGrassTypes, setSelectedFairwayGrassTypes] = useState(
    []
  );
  const [selectedGreenGrassTypes, setSelectedGreenGrassTypes] = useState([]);
  const [selectedRoughGrassTypes, setSelectedRoughGrassTypes] = useState([]);
  const [numRounds, setNumRounds] = useState(undefined);
  const [minRounds, setMinRounds] = useState(undefined);
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);
  const [minYards, setMinYards] = useState(undefined);
  const [maxYards, setMaxYards] = useState(undefined);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [coursePars, setCoursePars] = useState([]);
  const [selectedDkPlayerId, setSelectedDkPlayerId] = useState(undefined);

  const [viewRanks, setViewRanks] = useState(true);

  const [filterSaves, setFilterSaves] = useState(0);
  const [filterLoads, setFilterLoads] = useState(0);

  function pushInputUpdate(input) {
    if (!pgaTourModelId) {
      return;
    }

    commitMutation(relayEnvironment, {
      mutation: UPDATE_INPUTS,
      variables: {
        input: { modelId: pgaTourModelId, ...input },
      },
      onCompleted: (resp) => {
        const success = resp.pgaTourModelUpdateInputs.success;

        if (success) {
          setInputReloads(inputReloads + 1);
        }
      },
    });
  }

  function setAndUpdateSelectedFairwayGrassTypes(selected) {
    setSelectedFairwayGrassTypes(selected);

    const idsArray = selected.map((type) => type.value);

    pushInputUpdate({ fairwayGrassTypeIds: idsArray });
  }

  function setAndUpdateSelectedGreenGrassTypes(selected) {
    setSelectedGreenGrassTypes(selected);

    const idsArray = selected.map((type) => type.value);

    pushInputUpdate({ greenGrassTypeIds: idsArray });
  }

  function setAndUpdateSelectedRoughGrassTypes(selected) {
    setSelectedRoughGrassTypes(selected);

    const idsArray = selected.map((type) => type.value);

    pushInputUpdate({ roughGrassTypeIds: idsArray });
  }

  function setAndUpdateNumRounds(selected) {
    setNumRounds(selected);
    pushInputUpdate({ numRounds: Number.parseInt(selected) });
  }

  function setAndUpdateMinRounds(selected) {
    setMinRounds(selected);
    pushInputUpdate({ minRounds: Number.parseInt(selected) });
  }

  function setAndUpdateFromDate(selected) {
    setFromDate(selected);
    pushInputUpdate({ fromDate: selected });
  }

  function setAndUpdateToDate(selected) {
    setToDate(selected);
    pushInputUpdate({ toDate: selected });
  }

  function setAndUpdateMinYards(selected) {
    setMinYards(selected);
    pushInputUpdate({ minYards: Number.parseInt(selected) });
  }

  function setAndUpdateMaxYards(selected) {
    setMaxYards(selected);
    pushInputUpdate({ maxYards: Number.parseInt(selected) });
  }

  function setAndUpdateSelectedCourses(selected) {
    setSelectedCourses(selected);
    const idsArray = selected.map((type) => type.value);
    pushInputUpdate({ courseIds: idsArray });
  }

  function setAndUpdateCoursePars(selected) {
    setCoursePars(selected);
    pushInputUpdate({ coursePars: selected });
  }

  const inputProps = {
    selectedFairwayGrassTypes,
    setAndUpdateSelectedFairwayGrassTypes,
    selectedGreenGrassTypes,
    setAndUpdateSelectedGreenGrassTypes,
    selectedRoughGrassTypes,
    setAndUpdateSelectedRoughGrassTypes,
    numRounds,
    setAndUpdateNumRounds,
    minRounds,
    setAndUpdateMinRounds,
    fromDate,
    setAndUpdateFromDate,
    toDate,
    setAndUpdateToDate,
    minYards,
    setAndUpdateMinYards,
    maxYards,
    setAndUpdateMaxYards,
    selectedCourses,
    setAndUpdateSelectedCourses,
    coursePars,
    setAndUpdateCoursePars,
  };

  const [calcReloads, setCalcReloads] = useState(0);
  const [filterReloads, setFilterReloads] = useState(0);
  const [addFilterCurrentValue, setAddFilterCurrentValue] = useState("");
  const [showAddFilterOverlay, setAddFilterOverlay] = useState(false);
  const [showLoadFilterOverlay, setShowLoadFilterOverlay] = useState(false);
  const [featureOutputReloads, setFeatureOutputReloads] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");

  function buildModelSucceeded() {
    setFeatureOutputReloads(featureOutputReloads + 1);
  }

  function addCalcToModel(newCalcName, newCalcId) {
    if (loading || !newCalcName || !newCalcId) return;

    if (!pgaTourModelId) {
      alert("You must select a slate before adding a calculation.");

      return;
    }

    setLoadingMessage("Adding calc...");
    setLoading(true);

    commitMutation(relayEnvironment, {
      mutation: ADD_CALC,
      variables: {
        input: {
          modelId: pgaTourModelId,
          calcName: newCalcName,
          calcId: newCalcId,
        },
      },
      onCompleted: (content, errors) => {
        if (!errors) {
          setCalcReloads(calcReloads + 1);
        }

        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
      },
    });
  }

  function handleSlateChange(selected) {
    setLoading(true);
    setLoadingMessage("Getting slate...");
    setSelectedSlateId(selected.value);

    commitMutation(relayEnvironment, {
      mutation: GET_OR_CREATE_MODEL,
      variables: {
        input: {
          slateId: selected.value,
        },
      },
      onCompleted: (content, errors) => {
        if (errors || !content?.pgaTourGetOrCreateModelForSlate?.model) {
          const errorMessage =
            errors?.[0]?.message || "Failed to load slate. Please try again.";
          alert(errorMessage);
          setLoading(false);
          return;
        }

        const model = content.pgaTourGetOrCreateModelForSlate.model;

        setSelectedFairwayGrassTypes(
          model.fairwayGrassTypes?.edges?.map((grassType) => {
            return {
              label: grassType.node.categoryName,
              value: grassType.node.id,
            };
          }) || []
        );

        setSelectedRoughGrassTypes(
          model.roughGrassTypes?.edges?.map((grassType) => {
            return {
              label: grassType.node.categoryName,
              value: grassType.node.id,
            };
          }) || []
        );

        setSelectedGreenGrassTypes(
          model.greenGrassTypes?.edges?.map((grassType) => {
            return {
              label: grassType.node.categoryName,
              value: grassType.node.id,
            };
          }) || []
        );

        setPGATourModelId(model.id);
        setCoursePars(model.coursePars);
        setNumRounds(model.numRounds);
        setMinRounds(model.minRounds);
        setFromDate(model.fromDate);
        setToDate(model.toDate);
        setMinYards(model.minYards);
        setMaxYards(model.maxYards);

        setSelectedCourses(
          model.courses?.edges?.map((course) => {
            return {
              label: course.node.name,
              value: course.node.id,
            };
          }) || []
        );

        setLoading(false);
      },
      onError: (error) => {
        alert(error?.message || "An error occurred while loading the slate.");
        setLoading(false);
      },
    });
  }

  const { data, loading: whoAmILoading } = useQuery(GET_WHO_AM_I);

  if (whoAmILoading) return <></>;
  if (!data.whoAmI || !isToolSub(data.whoAmI.groups)) {
    return <Redirect to="/memberships" />;
  }

  function showDKPlayerInfo(dkPlayerId) {
    if (dkPlayerId) {
      setSelectedDkPlayerId(dkPlayerId);
    }
  }

  function hideDKPlayerInfo() {
    setSelectedDkPlayerId(undefined);
  }

  function resetModel() {
    if (
      !confirm(
        "Are you sure you want to reset your model? This cannot be undone."
      )
    ) {
      return;
    }

    setModelResetting(true);

    commitMutation(relayEnvironment, {
      mutation: RESET_MODEL,
      variables: {
        input: {
          modelId: pgaTourModelId,
        },
      },
      onCompleted: (resp) => {
        const success = resp.pgaTourResetModel.success;

        if (success) {
          location.reload();
        }

        setModelResetting(false);
      },
    });
  }

  function saveFilters() {
    if (!addFilterCurrentValue) {
      alert("You must provide a name for the filter set.");
      return;
    }

    setModelResetting(true);

    commitMutation(relayEnvironment, {
      mutation: SAVE_FILTERS,
      variables: {
        input: {
          modelId: pgaTourModelId,
          name: addFilterCurrentValue,
        },
      },
      onCompleted: (resp) => {
        const success = resp.pgaTourSaveModelFilters?.success;

        setModelResetting(false);
        setAddFilterOverlay(false);

        if (!success) {
          alert("Failed to save filters.");
        } else {
          alert(`Saved your filters as ${addFilterCurrentValue}`);

          setFilterSaves(filterSaves + 1);
          setAddFilterCurrentValue("");
        }
      },
    });
  }

  function loadFilter(filterId) {
    if (!pgaTourModelId || !filterId) {
      return;
    }

    commitMutation(relayEnvironment, {
      mutation: LOAD_INPUT,
      variables: {
        input: { pgaTourModelId: pgaTourModelId, savedModelFilterId: filterId },
      },
      onCompleted: (resp) => {
        const success = resp.pgaTourLoadSavedModelFilter.success;

        if (success) {
          setInputReloads(inputReloads + 1);

          handleSlateChange({ value: selectedSlateId });

          setFilterLoads(filterLoads + 1);

          setShowLoadFilterOverlay(false);
        } else {
          alert("Failed to load filter.");
        }
      },
    });
  }

  return (
    <>
      <div className="pga-model-builder-page-container">
        <DraftkingsInfoOverlay
          show={selectedDkPlayerId !== undefined}
          playerId={selectedDkPlayerId}
          close={hideDKPlayerInfo}
        />
        <h1>PGA Model</h1>
        <div className="pga-model-builder-container">
          <LoadingOverlay
            spinner
            active={loading}
            text={loadingMessage}
            style={{ width: "100%" }}
            label="Model Setup"
          >
            <div className="pga-model-builder-tab">
              <PGAModelBuilderSection widthType={"large"}>
                <h3>Step 1 - Select which slate you want to model</h3>
                <div style={{ width: "100%" }}>
                  <React.Suspense fallback={"Loading slates..."}>
                    <PGAModelBuilderSlateSelect onChange={handleSlateChange} />
                  </React.Suspense>
                </div>
              </PGAModelBuilderSection>
              <ShowHide
                retractMessage="Hide Model Configuration"
                expandMessage="Configure Model"
                secondCloseAtBottom={true}
              >
                <>
                  <br />
                  <div style={{ width: "150px" }}>
                    <PGAModelBuilderButton
                      style={{ backgroundColor: "#ff5964", color: "white" }}
                      onClick={resetModel}
                      modelBuilding={modelResetting}
                    >
                      Reset Model
                    </PGAModelBuilderButton>
                  </div>
                  <br />
                  <div className="pga-model-builder-field-select">
                    <SelectField
                      options={["All Players", "Field Only"]}
                      selected={selectedFieldOption}
                      setSelected={setSelectedFieldOption}
                    />
                  </div>
                  <PGAModelBuilderSection widthType={"large"}>
                    <h3 style={{ width: "100%" }}>
                      Step 2 - Add features to your model
                    </h3>
                    <React.Suspense fallback={"Loading calcs..."}>
                      <PGAModelBuilderNewCalcSelect
                        addCalcToModel={addCalcToModel}
                        calcReloads={calcReloads}
                      />
                    </React.Suspense>
                  </PGAModelBuilderSection>
                  {pgaTourModelId ? (
                    <PGAModelBuilderSection widthType={"large"}>
                      <h3>
                        Step 3 - Configure inputs for your model tabs, then
                        click the red button to build your model!
                        <br />
                        <Overlay show={showAddFilterOverlay}>
                          <div
                            style={{
                              background: "white",
                              margin: "auto",
                              width: "400px",
                              height: "150px",
                              maxWidth: "95%",
                            }}
                          >
                            <XMark
                              className="feature-icon"
                              fill="black"
                              onClick={() => setAddFilterOverlay(false)}
                              style={{
                                cursor: "pointer",
                                display: "inline",
                                margin: "10 10 10 10",
                              }}
                            />
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                background: "white",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                              }}
                            >
                              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                              <label
                                style={{
                                  maxHeight: "100px",
                                  width: "80%",
                                  textAlign: "center",
                                }}
                              >
                                Set a name for this filter set:
                                <br />
                                <br />
                                <PGAModelBuilderInput
                                  type="text"
                                  placeholder="Filter Name"
                                  onChange={(e) => {
                                    setAddFilterCurrentValue(e.target.value);
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      saveFilters();
                                    }
                                  }}
                                  value={addFilterCurrentValue}
                                />
                              </label>
                              <br />
                              <button onClick={saveFilters}>Save Filter</button>
                            </div>
                          </div>
                        </Overlay>
                        <button onClick={() => setAddFilterOverlay(true)}>
                          Save Inputs
                        </button>
                        {"   "}
                        <Overlay show={showLoadFilterOverlay}>
                          <div
                            style={{
                              background: "white",
                              margin: "auto",
                              width: "800px",
                              padding: "10px",
                              maxWidth: "95%",
                            }}
                          >
                            <XMark
                              className="feature-icon"
                              fill="black"
                              onClick={() => setShowLoadFilterOverlay(false)}
                              style={{
                                cursor: "pointer",
                                display: "inline",
                              }}
                            />
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                background: "white",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                              }}
                            >
                              <h2>Select A Filter To Load</h2>

                              <React.Suspense fallback="Loading filters...">
                                <PGAModelBuilderLoadFilters
                                  loadFilter={loadFilter}
                                  filterSaves={filterSaves}
                                />
                              </React.Suspense>
                            </div>
                          </div>
                        </Overlay>
                        <button onClick={() => setShowLoadFilterOverlay(true)}>
                          Load Inputs
                        </button>
                      </h3>
                      <React.Suspense fallback="Loading inputs...">
                        <PGAModelBuilderInputConfig
                          modelId={pgaTourModelId}
                          filterLoads={filterLoads}
                          {...inputProps}
                          buildSucceeded={buildModelSucceeded}
                        />
                      </React.Suspense>
                    </PGAModelBuilderSection>
                  ) : null}
                  <br />
                  <React.Suspense fallback="Loading calculations...">
                    <PGAModelBuilderCalcTabs
                      selectedSlateId={selectedSlateId}
                      modelId={pgaTourModelId}
                      calcReloads={calcReloads}
                      setCalcReloads={setCalcReloads}
                      featureOutputReloads={featureOutputReloads}
                      inputReloads={inputReloads}
                    />
                  </React.Suspense>
                </>
              </ShowHide>
            </div>
          </LoadingOverlay>
        </div>
        <br />
        <div className="pga-model-builder-container">
          {pgaTourModelId ? (
            <React.Suspense fallback={<div>Loading model output...</div>}>
              <PGAModelBuilderOutput
                modelID={pgaTourModelId}
                outputReloads={featureOutputReloads}
                viewRanks={viewRanks}
                setViewRanks={setViewRanks}
                showDKPlayerInfo={showDKPlayerInfo}
              />
            </React.Suspense>
          ) : (
            <div>Select slate to see output.</div>
          )}
        </div>
        <div className="pga-model-builder-container"></div>
      </div>
    </>
  );
}
